import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { SCREEN_SIZE, useResponsiveness } from "hooks/useResponsiveness";
import useTranslationHook from "hooks/useTranslationHook";
import wrapperHOC from "@Utils/wrapperHOC";
import { AppState } from "@Store/index";
import { GROWTH_BOOK_FEATURE_FLAG_NAMES } from "constants/index";
import PropertyPageDispatcher from "../../data/actions";
import useFloorPlanData from "./hooks/useFloorPlanData";
import useSitePlanData from "./hooks/useSitePlanData";
import SectionContainer from "../SectionContainer";
import SideNavigation from "./components/SideNavigation";
import ImageViewer from "./components/ImageViewer";
import InfoBar from "./components/InfoBar";
import SegmentedControl from "./components/SegmentedControl";
import RoomTypeDropdown from "./components/RoomTypeDropdown";
import { FloorPlanImage, formatAltText, getDefaultRoomTypeId } from "./utils/floorPlanHelpers";
import unitTypesDescription from "../RoomTypeSection/utils/unitTypesDescription";
import FloorPlanIcon from "@Icons/FloorPlanIcon";
import ChevronLeftIcon from "@Icons/ChevronLeftIcon";
import ChevronRightIcon from "@Icons/ChevronRightIcon";
import styles from "./FloorPlansSection.module.scss";
import config from "@Config/index";

const FloorPlansSection = ({ isDesktop = false }: { isDesktop?: boolean }) => {
  const dispatch = useDispatch();
  const propertyPageDispatcher = new PropertyPageDispatcher(dispatch);
  const { roomTypes, hasFloorPlans } = useFloorPlanData();
  const { sitePlan, hasSitePlan, propertyLayoutImages, hasPropertyLayout } = useSitePlanData();
  const { isAbove } = useResponsiveness();
  const { t } = useTranslationHook();
  const isFloorPlansSectionEnabled =
    useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.SHOW_FLOOR_PLANS_SECTION) || config.IS_CHINA_APP;

  const isDesktopViewport = isAbove(SCREEN_SIZE.MD);

  // hasSitePlan OR hasPropertyLayout → show as "Site Plan" / "Property Layout"
  const hasSitePlanOrLayout = hasSitePlan || hasPropertyLayout;

  const [activeRoomTypeId, setActiveRoomTypeId] = useState<string | null>(() =>
    hasSitePlanOrLayout ? null : getDefaultRoomTypeId(roomTypes),
  );
  const [isSitePlanActive, setIsSitePlanActive] = useState(hasSitePlanOrLayout);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [mobileView, setMobileView] = useState<"floor_plan" | "site_plan">(
    hasSitePlanOrLayout ? "site_plan" : "floor_plan",
  );

  // Track carousel show state for syncing index on close
  const carouselShow = useSelector(
    ({ propertyPageData }: AppState) => propertyPageData?.gallery_carousel?.show,
  );
  const carouselIndex = useSelector(
    ({ propertyPageData }: AppState) => propertyPageData?.gallery_carousel?.currentItemIndex,
  );
  const prevCarouselShow = useRef(carouselShow);

  // Set the default active item once data is available.
  // This handles the case where roomTypes data loads after initial mount
  // (e.g. floor plan images only available in the full room types response).
  // Also resets activeRoomTypeId if it no longer matches any current room type
  // (e.g. after client-side navigation to a different property).
  useEffect(() => {
    if (!activeRoomTypeId && !isSitePlanActive) {
      if (hasSitePlanOrLayout) {
        setIsSitePlanActive(true);
        setMobileView("site_plan");
      } else if (roomTypes.length > 0) {
        const defaultId = getDefaultRoomTypeId(roomTypes);
        if (defaultId) {
          setActiveRoomTypeId(defaultId);
          setMobileView("floor_plan");
        }
      }
    } else if (activeRoomTypeId && roomTypes.length > 0) {
      // If the active ID no longer exists in the current room types (e.g. navigated
      // to a different property), reset to the first available room type.
      const stillExists = roomTypes.some((rt) => rt.id === activeRoomTypeId);
      if (!stillExists) {
        const defaultId = getDefaultRoomTypeId(roomTypes);
        if (defaultId) {
          setActiveRoomTypeId(defaultId);
          setCurrentImageIndex(0);
        } else {
          setActiveRoomTypeId(null);
        }
      }
    }
  }, [roomTypes, hasSitePlanOrLayout, activeRoomTypeId, isSitePlanActive]);

  // Notify SectionNavigation that this section is now in the DOM.
  // We check that the section will actually render (not blocked by guards)
  // before dispatching the event.
  const willRender =
    isFloorPlansSectionEnabled &&
    (hasFloorPlans || hasSitePlanOrLayout) &&
    (activeRoomTypeId || isSitePlanActive || hasSitePlanOrLayout);
  useEffect(() => {
    if (typeof window !== "undefined" && willRender) {
      window.dispatchEvent(new Event("navigation_section_removed"));
    }
  }, [willRender]);

  const activeRoomType = useMemo(
    () => roomTypes.find((rt) => rt.id === activeRoomTypeId) || null,
    [roomTypes, activeRoomTypeId],
  );

  const currentImages: FloorPlanImage[] = useMemo(() => {
    if (isSitePlanActive) {
      // If we have actual site_plan data (building-level floor details), use that
      if (hasSitePlan) {
        return sitePlan.flatMap((building) =>
          building.floor_details.map((detail) => ({
            base_path: detail.image,
            path: detail.image,
            type: "floor_plan" as const,
          })),
        );
      }
      // Otherwise use property-level floor_plan images (Property Layout)
      if (hasPropertyLayout) {
        return propertyLayoutImages;
      }
    }
    return activeRoomType?.images || [];
  }, [
    isSitePlanActive,
    sitePlan,
    activeRoomType,
    hasSitePlan,
    hasPropertyLayout,
    propertyLayoutImages,
  ]);

  // Sync currentImageIndex when the gallery carousel is closed.
  // If the user navigated to a different image inside the carousel,
  // we update our local index to match so the section stays in sync.
  useEffect(() => {
    if (prevCarouselShow.current && !carouselShow) {
      if (
        typeof carouselIndex === "number" &&
        carouselIndex >= 0 &&
        carouselIndex < currentImages.length
      ) {
        setCurrentImageIndex(carouselIndex);
      }
    }
    prevCarouselShow.current = carouselShow;
  }, [carouselShow, carouselIndex, currentImages.length]);

  const handleRoomTypeSelect = (id: string) => {
    setActiveRoomTypeId(id);
    setIsSitePlanActive(false);
    setCurrentImageIndex(0);
  };

  const handleSitePlanSelect = () => {
    setIsSitePlanActive(true);
    setActiveRoomTypeId(null);
    setCurrentImageIndex(0);
  };

  const handleMobileViewChange = (view: "floor_plan" | "site_plan") => {
    setMobileView(view);
    setIsSitePlanActive(view === "site_plan");
    setCurrentImageIndex(0);
    if (view === "floor_plan") {
      if (!activeRoomTypeId && roomTypes.length > 0) {
        const defaultId = getDefaultRoomTypeId(roomTypes);
        if (defaultId) setActiveRoomTypeId(defaultId);
      }
    } else {
      setActiveRoomTypeId(null);
    }
  };

  const handleNext = () => {
    setCurrentImageIndex((prev) => Math.min(prev + 1, currentImages.length - 1));
  };

  const handlePrev = () => {
    setCurrentImageIndex((prev) => Math.max(prev - 1, 0));
  };

  const handleImageClick = () => {
    if (currentImages.length === 0) return;
    const carouselImages = currentImages.map((img) => ({
      base_path: img.base_path,
      path: img.path,
      type: "floor_plan",
    }));
    propertyPageDispatcher.showGalleryCarousel({
      show: true,
      images: carouselImages,
      category: "floor-plan",
      currentItemIndex: currentImageIndex,
      showViewRoomButton: false,
    });
  };

  const handleCtaClick = () => {
    if (isSitePlanActive) {
      return;
    }
    if (activeRoomType) {
      const target = document.getElementById(`room-${activeRoomType.id}`);
      if (target) {
        target.scrollIntoView({ behavior: "smooth" });
        return;
      }
      // Room card not visible — it may be behind "View Sold Out Rooms".
      // Room types are grouped by unit type, each with its own sold-out toggle,
      // so click every collapsed sold-out toggle to reveal all hidden cards
      // (we don't know which group the target room belongs to). We target a
      // stable data-testid (not the translated button text) and only click
      // toggles that are still collapsed to avoid hiding already-open groups.
      const soldOutToggles = document.querySelectorAll<HTMLElement>(
        '[data-testid="sold-out-rooms-toggle"][data-collapsed="true"]',
      );
      soldOutToggles.forEach((toggle) => toggle.click());

      // The revealed cards render asynchronously after the state update.
      // Poll for the card instead of relying on a single fixed timeout, which
      // can fire before React has committed the newly revealed cards to the DOM
      // (this is why it failed on the first click but worked afterwards).
      let attempts = 0;
      const maxAttempts = 30; // ~3s at 100ms intervals
      const intervalId = setInterval(() => {
        attempts += 1;
        const el = document.getElementById(`room-${activeRoomType.id}`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth" });
          clearInterval(intervalId);
        } else if (attempts >= maxAttempts) {
          clearInterval(intervalId);
        }
      }, 100);
    }
  };

  const infoBarData = useMemo(() => {
    if (isSitePlanActive) {
      // Property layout images (no building/floor structure)
      if (!hasSitePlan && hasPropertyLayout) {
        return {
          name: t("propertypage.floorplanssection.sidenavigation.unitlayout"),
          area: null,
          floor:
            currentImageIndex >= 0
              ? `${currentImageIndex + 1} / ${propertyLayoutImages.length}`
              : null,
          facing: null,
          ctaText: "",
          showCta: false,
          icon: <FloorPlanIcon />,
        };
      }
      // Site plan with building-level floor details
      let buildingName = "";
      let floorNumber = "";
      let count = 0;
      for (const building of sitePlan) {
        for (const detail of building.floor_details) {
          if (count === currentImageIndex) {
            buildingName = building.building;
            floorNumber = detail.floor;
            break;
          }
          count++;
        }
        if (buildingName) break;
      }
      return {
        name: "Site Plan",
        area: buildingName || null,
        floor: floorNumber ? `Floor ${floorNumber}` : null,
        facing: null,
        ctaText: "",
        showCta: false,
        icon: <FloorPlanIcon />,
      };
    }
    if (activeRoomType) {
      return {
        name: activeRoomType.name,
        area: activeRoomType.area ? `Area: ${activeRoomType.area}` : null,
        floor: activeRoomType.floor ? `Floor ${activeRoomType.floor}` : null,
        facing: activeRoomType.facing,
        ctaText: t("View Room"),
        showCta: true,
        icon:
          (unitTypesDescription(t) as Record<string, any>)[activeRoomType.unitType]?.icon || null,
      };
    }
    return {
      name: "",
      area: null,
      floor: null,
      facing: null,
      ctaText: "View Room",
      showCta: false,
      icon: null,
    };
  }, [
    isSitePlanActive,
    sitePlan,
    currentImageIndex,
    activeRoomType,
    hasSitePlan,
    hasPropertyLayout,
    propertyLayoutImages,
    t,
  ]);

  const altText = useMemo(() => {
    if (isSitePlanActive) {
      if (!hasSitePlan && hasPropertyLayout) {
        return formatAltText("Property Layout", null);
      }
      const allFloorDetails = sitePlan.flatMap((building) => building.floor_details);
      return formatAltText("Site Plan", allFloorDetails[currentImageIndex]?.floor || null);
    }
    if (activeRoomType) return formatAltText(activeRoomType.name, activeRoomType.floor);
    return "Floor plan";
  }, [
    isSitePlanActive,
    sitePlan,
    currentImageIndex,
    activeRoomType,
    hasSitePlan,
    hasPropertyLayout,
  ]);

  if (!isFloorPlansSectionEnabled) return null;
  if (!hasFloorPlans && !hasSitePlanOrLayout) return null;
  if (!activeRoomTypeId && !isSitePlanActive && !hasSitePlanOrLayout) return null;

  return (
    <div id="floor-layout" data-testid="floor-layout-section">
      <SectionContainer
        title={isDesktopViewport ? t("propertypage.components.actionbuttonsection.floorplan") : ""}
        className={styles.floorPlansSection}
        isDesktop={isDesktop}
        dataTestId="floor-plans-section-container"
      >
        {isDesktopViewport ? (
          <div className={styles.desktopLayout} data-testid="floor-plans-desktop-layout">
            <div className={styles.sideNavigationColumn} data-testid="floor-plans-side-nav">
              <SideNavigation
                roomTypes={roomTypes}
                sitePlan={sitePlan}
                hasPropertyLayout={hasPropertyLayout}
                activeRoomTypeId={activeRoomTypeId}
                isSitePlanActive={isSitePlanActive}
                onRoomTypeSelect={handleRoomTypeSelect}
                onSitePlanSelect={handleSitePlanSelect}
              />
            </div>
            <div className={styles.mainColumn} data-testid="floor-plans-main-column">
              <div className={styles.imageViewerWrapper} data-testid="floor-plans-image-viewer">
                <div className={styles.dottedBackground} />
                <div className={styles.floorPlanImageContainer}>
                  <ImageViewer
                    images={currentImages}
                    currentIndex={currentImageIndex}
                    onNext={handleNext}
                    onPrev={handlePrev}
                    altText={altText}
                    showChevrons={false}
                    onImageClick={handleImageClick}
                  />
                </div>
                {currentImages.length > 1 && (
                  <>
                    <button
                      type="button"
                      className={`${styles.chevronOverlay} ${styles.chevronLeft}`}
                      onClick={currentImageIndex === 0 ? undefined : handlePrev}
                      aria-label="Previous floor plan"
                      disabled={currentImageIndex === 0}
                    >
                      <ChevronLeftIcon width="24" height="24" />
                    </button>
                    <button
                      type="button"
                      className={`${styles.chevronOverlay} ${styles.chevronRight}`}
                      onClick={
                        currentImageIndex === currentImages.length - 1 ? undefined : handleNext
                      }
                      aria-label="Next floor plan"
                      disabled={currentImageIndex === currentImages.length - 1}
                    >
                      <ChevronRightIcon width="24" height="24" />
                    </button>
                  </>
                )}
              </div>
              <InfoBar
                name={infoBarData.name}
                area={infoBarData.area}
                floor={infoBarData.floor}
                facing={infoBarData.facing}
                ctaText={infoBarData.ctaText}
                onCtaClick={handleCtaClick}
                showCta={infoBarData.showCta}
                icon={infoBarData.icon}
              />
            </div>
          </div>
        ) : (
          <div className={styles.mobileLayout} data-testid="floor-plans-mobile-layout">
            <div className={styles.mobileHeader} data-testid="floor-plans-mobile-header">
              <h4 className={styles.mobileTitle}>
                {t("propertypage.components.actionbuttonsection.floorplan")}
              </h4>
              {hasSitePlanOrLayout && hasFloorPlans && (
                <SegmentedControl activeView={mobileView} onViewChange={handleMobileViewChange} />
              )}
            </div>

            <div className={styles.mobileBody} data-testid="floor-plans-mobile-body">
              {mobileView === "floor_plan" && (
                <RoomTypeDropdown
                  roomTypes={roomTypes}
                  selectedId={activeRoomTypeId}
                  onSelect={handleRoomTypeSelect}
                />
              )}

              <div className={styles.mobileImageBox} data-testid="floor-plans-mobile-image-section">
                <div className={styles.mobileImageArea}>
                  <div className={styles.dottedBackground} />
                  <ImageViewer
                    images={currentImages}
                    currentIndex={currentImageIndex}
                    onNext={handleNext}
                    onPrev={handlePrev}
                    altText={altText}
                    showChevrons={false}
                    onImageClick={handleImageClick}
                  />
                  {currentImages.length > 1 && (
                    <>
                      <button
                        type="button"
                        className={`${styles.mobileChevron} ${styles.mobileChevronLeft}`}
                        onClick={currentImageIndex === 0 ? undefined : handlePrev}
                        aria-label="Previous floor plan"
                        disabled={currentImageIndex === 0}
                      >
                        <ChevronLeftIcon width="20" height="20" />
                      </button>
                      <button
                        type="button"
                        className={`${styles.mobileChevron} ${styles.mobileChevronRight}`}
                        onClick={
                          currentImageIndex === currentImages.length - 1 ? undefined : handleNext
                        }
                        aria-label="Next floor plan"
                        disabled={currentImageIndex === currentImages.length - 1}
                      >
                        <ChevronRightIcon width="20" height="20" />
                      </button>
                    </>
                  )}
                </div>
                <div className={styles.mobileCtaBar} data-testid="floor-plans-mobile-cta-bar">
                  <div className={styles.ctaInfo}>
                    <span className={styles.ctaName}>{infoBarData.name}</span>
                    <div className={styles.ctaDetails}>
                      {infoBarData.area && (
                        <span className={styles.ctaDetail}>{infoBarData.area}</span>
                      )}
                      {infoBarData.area && infoBarData.floor && (
                        <span className={styles.ctaDetailDot} />
                      )}
                      {infoBarData.floor && (
                        <span className={styles.ctaDetail}>{infoBarData.floor}</span>
                      )}
                    </div>
                  </div>
                  {infoBarData.showCta && (
                    <button
                      type="button"
                      className={styles.ctaButton}
                      onClick={handleCtaClick}
                      aria-label={infoBarData.ctaText}
                    >
                      {infoBarData.ctaText}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </SectionContainer>
    </div>
  );
};

export default wrapperHOC(FloorPlansSection, {
  componentName: "FloorPlansSection-PropertyPage",
  showForChina: true,
});
